import React, { useState, useMemo } from 'react';
import { Card, Button, Form, InputGroup } from 'react-bootstrap';
import { FiSearch, FiPlus } from 'react-icons/fi';
import { useLanguage } from '../../../../../API/Context/LanguageContext';
import { useUser } from '../../../../../API/Context/UserContext';
import { useActivePatient } from '../../../../../API/Hooks/ActivePatientContext';
import type { AllergyResponse } from '../../../../../Types/schema-helpers';
import { AllergyPill } from './subwidgets/AllergyPill';
import { AllergyDetailModal } from './subwidgets/AllergyDetailModal';
import { CreateAllergyModal } from './subwidgets/CreateAllergyModal';
import EnumSelect from '../../../LoggedUserGeneral/ProfileOnboarding/Steps/Widgets/EnumSelect';
import { ALLERGY_CATEGORY_MAP } from '../../../../../ConfigFiles/maps/allergies_map';

export const AllergiesWidget: React.FC = () => {
  const { lan } = useLanguage();
  const currentLanKey = (lan as number) === 0 ? 0 : 1;
  const { isProfessional } = useUser();
  const { activePatient } = useActivePatient();

  // Read data directly from ActivePatientContext
  const allergies: AllergyResponse[] = useMemo(() => {
    return activePatient?.health_background?.allergies ?? [];
  }, [activePatient]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [activeAllergy, setActiveAllergy] = useState<AllergyResponse | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const filteredAllergies = useMemo(() => {
    return allergies.filter((a) => {
      const matchesSearch = a.allergen.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === null || a.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [allergies, searchQuery, selectedCategory]);

  return (
    <Card className="border-0 shadow-sm p-3">
      {/* 1. Filter and Search Bar (Fixed Side-by-Side Grid) */}
      <div className="row g-2 mb-3 align-items-center">
        {/* Search Bar - Fixed Left Position */}
        <div className="col-12 col-sm-6 col-md-5">
          <InputGroup size="sm">
            <Form.Control
              placeholder={currentLanKey === 0 ? 'Buscar alergia...' : 'Search allergy...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <InputGroup.Text className="bg-white border-start-0 text-muted">
              <FiSearch size={15} />
            </InputGroup.Text>
          </InputGroup>
        </div>

        {/* Category Dropdown - Fixed Right Position with Built-in Clear */}
        <div className="col-12 col-sm-6 col-md-4">
          <EnumSelect
            value={selectedCategory}
            options={ALLERGY_CATEGORY_MAP}
            onChange={(val) => setSelectedCategory(typeof val === 'number' ? val : null)}
            placeholder={currentLanKey === 0 ? 'Todas las categorías' : 'All Categories'}
            size="sm"
            clearable={true}
          />
        </div>
      </div>

      {/* 2. Allergy Pills Container */}
      <div className="d-flex flex-wrap gap-2 align-items-center py-2 min-vh-10">
        {filteredAllergies.length === 0 ? (
          <p className="text-muted small fst-italic mb-0 py-2">
            {currentLanKey === 0 ? 'No hay alergias registradas.' : 'No allergy records found.'}
          </p>
        ) : (
          filteredAllergies.map((allergy) => (
            <AllergyPill
              key={allergy.allergy_id}
              allergy={allergy}
              isSelected={activeAllergy?.allergy_id === allergy.allergy_id}
              onClick={() => setActiveAllergy(allergy)}
            />
          ))
        )}
      </div>

      {/* 3. Professional Action Button */}
      {isProfessional && (
        <div className="d-flex justify-content-end mt-3 border-top pt-2">
          <Button
            variant="outline-primary"
            size="sm"
            className="d-flex align-items-center gap-1 rounded-pill px-3"
            onClick={() => setShowCreateModal(true)}
          >
            <FiPlus size={16} />
            <span>{currentLanKey === 0 ? 'Agregar Alergia' : 'Add Allergy'}</span>
          </Button>
        </div>
      )}

      {/* Detail & Create Modals */}
      <AllergyDetailModal
        allergy={activeAllergy}
        show={!!activeAllergy}
        onHide={() => setActiveAllergy(null)}
        isProfessional={isProfessional}
      />

      <CreateAllergyModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
      />
    </Card>
  );
};